import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiBellOutline, mdiChevronLeft, mdiPlus, mdiDeleteOutline } from "@mdi/js";
import Select from "react-select";

import { useToast } from "../../context/ToastContext";
import { getAlertasAlerta, getGetAlertasAlertaQueryKey, postAlertasCadastrar, postAlertasAtualizar, getGruposListar, getGetGruposListarQueryKey } from "../../config/swagger";
import { SelectStyle } from "../../config/select";
import type { CadastrarAlertaRequest } from "../../model/cadastrarAlertaRequest";
import type { AtualizarAlertaRequest } from "../../model/atualizarAlertaRequest";
import type { CadastrarAlertaResponseIResponse } from "../../model/cadastrarAlertaResponseIResponse";
import type { AtualizarAlertaResponseIResponse } from "../../model/atualizarAlertaResponseIResponse";
import type { ExibirAlertaResponse } from "../../model/exibirAlertaResponse";

import Layout from "../../components/Layout";

const toastTitle = "Alerta";

export default function AlertaFormulario() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);
	const [showSmtp, setShowSmtp] = useState(false);
	const [showWhatsapp, setShowWhatsapp] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(getGetAlertasAlertaQueryKey({ id: Number(id) }), () => fetchData(id!), { enabled: !!id && !formStatus });
	const { data: gruposData } = useQuery(getGetGruposListarQueryKey(), () => getGruposListar());

	const { register, handleSubmit, reset, control } = useForm<ExibirAlertaResponse>({ defaultValues: data?.Data });

	const {
		fields: destinatariosFields,
		append: appendDestinatario,
		remove: removeDestinatario,
	} = useFieldArray({
		control,
		name: "destinatarios",
	});

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: string) {
		const resp = await getAlertasAlerta({ id: Number(id) });
		reset(resp.Data);
		setShowSmtp(!!resp.Data?.configuracoesSmtp);
		setShowWhatsapp(!!resp.Data?.whatsAppConfiguration);
		return resp;
	}

	async function mutatePost(data: CadastrarAlertaRequest) {
		setFormSaving(true);
		return await postAlertasCadastrar(data);
	}

	function mutatePostSuccess(resp: CadastrarAlertaResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		if (resp.Data?.id) {
			navigate("/alertaFormulario/" + resp.Data.id);
		}
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: AtualizarAlertaRequest) {
		setFormSaving(true);
		return await postAlertasAtualizar(data);
	}

	function mutatePutSuccess(resp: AtualizarAlertaResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					const payload = {
						nome: values.nome,
						descricao: values.descricao,
						gruposIds: values.gruposIds,
						configuracoesSmtp: showSmtp ? values.configuracoesSmtp : undefined,
						whatsAppConfiguration: showWhatsapp ? values.whatsAppConfiguration : undefined,
						destinatarios: values.destinatarios,
					};

					if (id) {
						mutationPut.mutate({
							id: Number(id),
							...payload,
						});
					} else {
						mutationPost.mutate(payload);
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/alertas"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiBellOutline} size={1} className="me-2" /> Formulário de Alerta
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{!formStatus && (
							<Button
								variant="dark"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/alertas");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Informações Básicas</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("nome", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="descricao">
							<Form.Label>Descrição</Form.Label>
							<Form.Control as="textarea" rows={3} placeholder="Informe aqui" {...register("descricao")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="gruposIds">
							<Form.Label>Grupos</Form.Label>
							<Controller
								name="gruposIds"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										isMulti
										options={gruposData?.Data?.map((grupo) => ({
											value: grupo.id,
											label: grupo.nome,
										}))}
										value={gruposData?.Data?.filter((grupo) => field.value?.includes(grupo.id!)).map((grupo) => ({
											value: grupo.id,
											label: grupo.nome,
										}))}
										onChange={(selected) => field.onChange(selected ? selected.map((item: any) => item.value) : [])}
										isDisabled={!formStatus}
										styles={SelectStyle}
										placeholder="Selecione os grupos..."
										noOptionsMessage={() => "Nenhum grupo disponível"}
									/>
								)}
							/>
						</Form.Group>
					</Card.Body>
				</Card>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
						Configurações de E-mail (SMTP)
						<Form.Check
							type="switch"
							id="smtp-toggle"
							className="d-flex gap-2"
							label="Habilitar"
							checked={showSmtp}
							onChange={(e) => setShowSmtp(e.target.checked)}
							disabled={!formStatus}
						/>
					</Card.Header>
					{showSmtp && (
						<Card.Body>
							<Row>
								<Col md={8}>
									<Form.Group className="mb-3" controlId="smtp-host">
										<Form.Label>Host</Form.Label>
										<Form.Control type="text" placeholder="smtp.example.com" {...register("configuracoesSmtp.host")} disabled={!formStatus} />
									</Form.Group>
								</Col>
								<Col md={4}>
									<Form.Group className="mb-3" controlId="smtp-port">
										<Form.Label>Porta</Form.Label>
										<Form.Control type="number" placeholder="587" {...register("configuracoesSmtp.port", { valueAsNumber: true })} disabled={!formStatus} />
									</Form.Group>
								</Col>
							</Row>
							<Row>
								<Col md={6}>
									<Form.Group className="mb-3" controlId="smtp-username">
										<Form.Label>Usuário</Form.Label>
										<Form.Control type="text" placeholder="usuario@example.com" {...register("configuracoesSmtp.username")} disabled={!formStatus} />
									</Form.Group>
								</Col>
								<Col md={6}>
									<Form.Group className="mb-3" controlId="smtp-password">
										<Form.Label>Senha</Form.Label>
										<Form.Control type="password" placeholder="••••••••" {...register("configuracoesSmtp.password")} disabled={!formStatus} />
									</Form.Group>
								</Col>
							</Row>
							<Row>
								<Col md={6}>
									<Form.Group className="mb-3" controlId="smtp-email">
										<Form.Label>E-mail do Remetente</Form.Label>
										<Form.Control type="email" placeholder="noreply@example.com" {...register("configuracoesSmtp.email")} disabled={!formStatus} />
									</Form.Group>
								</Col>
								<Col md={6}>
									<Form.Group className="mb-3" controlId="smtp-name">
										<Form.Label>Nome do Remetente</Form.Label>
										<Form.Control type="text" placeholder="Sistema de Alertas" {...register("configuracoesSmtp.name")} disabled={!formStatus} />
									</Form.Group>
								</Col>
							</Row>
							<Form.Group className="mb-3" controlId="smtp-ssl">
								<Form.Check type="checkbox" label="Usar SSL" className="d-flex gap-2" {...register("configuracoesSmtp.ssl")} disabled={!formStatus} />
							</Form.Group>
						</Card.Body>
					)}
				</Card>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
						Configurações de WhatsApp
						<Form.Check
							type="switch"
							id="whatsapp-toggle"
							className="d-flex gap-2"
							size={1}
							label="Habilitar"
							checked={showWhatsapp}
							onChange={(e) => setShowWhatsapp(e.target.checked)}
							disabled={!formStatus}
						/>
					</Card.Header>
					{showWhatsapp && (
						<Card.Body>
							<Form.Group className="mb-3" controlId="whatsapp-host">
								<Form.Label>Host</Form.Label>
								<Form.Control type="text" placeholder="https://api.whatsapp.com" {...register("whatsAppConfiguration.host")} disabled={!formStatus} />
							</Form.Group>
							<Form.Group className="mb-3" controlId="whatsapp-token">
								<Form.Label>Token de Autenticação</Form.Label>
								<Form.Control type="text" placeholder="Token de autenticação" {...register("whatsAppConfiguration.autenticationToken")} disabled={!formStatus} />
							</Form.Group>
						</Card.Body>
					)}
				</Card>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
						Destinatários
						<Button variant="secondary" size="sm" onClick={() => appendDestinatario({ name: "", email: "", phone: "" })} disabled={!formStatus}>
							<Icon path={mdiPlus} size={0.7} className="me-1" />
							Adicionar
						</Button>
					</Card.Header>
					<Card.Body>
						{destinatariosFields && destinatariosFields.length > 0 ? (
							<>
								<Row>
									<Col>
										<Form.Label>Nome do destinatário</Form.Label>
									</Col>
									<Col>
										<Form.Label>E-mail do destinatário</Form.Label>
									</Col>
									<Col>
										<Form.Label>Telefone do destinatário</Form.Label>
									</Col>
									<Col md="auto">
										<Button variant="light" className="invisible">
											<Icon path={mdiDeleteOutline} size={1} className="text-danger" />
										</Button>
									</Col>
								</Row>
								{destinatariosFields.map((field, index) => (
									<Row key={field.id} className="mb-2">
										<Col>
											<Form.Group controlId={`destinatario-name-${index}`}>
												<Form.Control type="text" placeholder="Nome do destinatário" {...register(`destinatarios.${index}.name` as const)} disabled={!formStatus} />
											</Form.Group>
										</Col>
										<Col>
											<Form.Group controlId={`destinatario-email-${index}`}>
												<Form.Control type="email" placeholder="E-mail do destinatário" {...register(`destinatarios.${index}.email` as const)} disabled={!formStatus} />
											</Form.Group>
										</Col>
										<Col>
											<Form.Group controlId={`destinatario-phone-${index}`}>
												<Form.Control type="text" placeholder="Telefone do destinatário" {...register(`destinatarios.${index}.phone` as const)} disabled={!formStatus} />
											</Form.Group>
										</Col>
										<Col md="auto">
											<Button variant="light" onClick={() => removeDestinatario(index)} disabled={!formStatus}>
												<Icon path={mdiDeleteOutline} size={1} className="text-danger" />
											</Button>
										</Col>
									</Row>
								))}
							</>
						) : (
							<p className="text-muted mb-0">Nenhum destinatário adicionado</p>
						)}
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/alertas");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
				</div>
			</Form>
		</Layout>
	);
}
